import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { loadAssociatedLenders } from 'store/orderCreation/associatedLendersDataSlice';
import { useAppDispatch } from 'store/store';
import { RootStore } from 'store/types';
import { ILenderCompanyResponse } from 'types/lenders/ILenderCompany';
import { Card, Grid, GridItem, ISelectOption, TextInput } from 'ui-toolkit';
import { ClientInformationForm } from './ClientInformationForm';
import { LenderInformationForm } from './LenderInformationForm';
import { Show } from '../shared/Show';
import { UUID } from '../../types/shared/UUID';
import { useListClientsQuery } from '../../API/clients/ClientCompanyApi';
import { useListLendersQuery } from '../../API/lenders/LenderCompanyApi';
import { NewOrderSpaceWrapping } from './wrappers/NewOrderSpaceWrapper';
import { SelectFieldTW } from '../../tailwindComponents/selectTW/SelectFieldTW';
import { DisplayServices, eServices } from '../../ui-toolkit/enums/eServices';
import { useFormikContext } from 'formik';
import { useGetUserType } from 'components/accessControl/hooks/useGetUserType';
import { optionsFromDisplay } from '../../ui-toolkit/enums/optionsFromDisplay';
import { eAccessControlUserType } from '../../ui-toolkit/enums/eAccessControlUserType';
import { ICreateOrderRequest } from '../../ui-toolkit/orders/interfaces/createOrder/ICreateOrderRequest';
import { IClientData } from '../../ui-toolkit/orders/interfaces/IClient';

export interface ILenderClientInformationContainerProps {
  canSelectClient: boolean;
  canSelectLender: boolean;
  setSelectedClientId: React.Dispatch<React.SetStateAction<UUID | undefined>>;
  isNewOrder?: boolean;
}

export const LenderClientInformationContainer: React.FC<ILenderClientInformationContainerProps> = ({
  canSelectClient,
  canSelectLender,
  setSelectedClientId,
  isNewOrder,
}) => {
  const { data: associatedLenders } = useSelector((store: RootStore) => store.associatedLendersDataSlice);
  const { data: clients = [] } = useListClientsQuery({}, { skip: !canSelectClient });
  const { data: lenders = [] } = useListLendersQuery({}, { skip: !canSelectLender });

  const userType = useGetUserType();
  const dispatch = useAppDispatch();

  const clientCompanyList: ISelectOption<string>[] = useMemo(() => {
    return clients.map((t: IClientData) => ({ label: t.name, value: t.id }));
  }, [clients]);

  const lenderCompanyList: ISelectOption<string>[] = useMemo(() => {
    return lenders.map((t: ILenderCompanyResponse) => ({ label: t.name, value: t.id }));
  }, [lenders]);
  const lenderOptions = !!associatedLenders.length ? associatedLenders : lenderCompanyList;
  const servicesOptions = useMemo(() => optionsFromDisplay(DisplayServices), []);

  const onClientSelected = useCallback(
    (clientId?: string) => {
      dispatch(loadAssociatedLenders({ clientId, userType }));
    },
    [userType, dispatch]
  );

  useEffect(() => {
    if (userType === eAccessControlUserType.Client) {
      dispatch(loadAssociatedLenders({ userType: userType }));
    }
  }, [userType, dispatch]);

  const formik = useFormikContext<ICreateOrderRequest>();
  const { services } = formik.values;

  return (
    <NewOrderSpaceWrapping>
      <Card data-test-id={`client-lender-information card`}>
        <NewOrderSpaceWrapping>
          <Grid columnCount={4}>
            <Show when={canSelectClient}>
              <GridItem>
                <ClientInformationForm
                  autoFocus
                  canSelectClient={canSelectClient}
                  clientCompanyList={clientCompanyList}
                  lenderCompanyList={lenderCompanyList}
                  onClientSelected={onClientSelected}
                  setSelectedClientId={setSelectedClientId}
                  isNewOrder={isNewOrder}
                />
              </GridItem>
            </Show>
            <GridItem columnStart={canSelectClient ? '' : 'span 4'}>
              <LenderInformationForm
                autoFocus={!canSelectClient}
                clientCompanyList={clientCompanyList}
                lenderOptions={lenderOptions}
                isNewOrder={isNewOrder}
              />
            </GridItem>
            <Show when={canSelectClient}>
              <SelectFieldTW
                isRequired
                isMultiple
                labelText={'Services'}
                name={'services'}
                placeholder={'Select a service'}
                options={servicesOptions}
                className={'h-10 text-left'}
              />
            </Show>
            <Show when={canSelectClient && services.length > 0 && services.every((s) => s === eServices.title)}>
              <TextInput label={`Settlement Agent`} value={'Preferred Settlement Agent'} readOnly isRequired />
            </Show>
          </Grid>
        </NewOrderSpaceWrapping>
      </Card>
    </NewOrderSpaceWrapping>
  );
};
